import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon } from '@atoms/index';
import { CartItem as CartItemComponent } from '@molecules/index';
import { useCartStore, getCartItemKey } from '@store/cartStore';
import { formatCurrency } from '@utils/currency';
import { cn } from '@utils/cn';
import type { CartItem } from '@/types';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDrawer = ({ isOpen, onClose }: CartDrawerProps) => {
  const navigate = useNavigate();
  const {
    items,
    restaurant,
    subtotal,
    deliveryFee,
    serviceFee,
    promotionDiscount,
    appliedPromotionName,
    total,
    updateItem,
    removeItem,
    clearCart,
    syncFromBackend,
  } = useCartStore();

  const isEmpty = items.length === 0;

  useEffect(() => {
    if (isOpen) {
      syncFromBackend().catch((err) => {
        console.error('Failed to sync cart when drawer opened:', err);
      });
    }
  }, [isOpen, syncFromBackend]);

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black bg-opacity-50 transition-opacity z-40',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={cn(
          'fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50',
          'transform transition-transform duration-300 flex flex-col',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <h2 className="text-xl font-bold text-gray-900">Sepetim</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900 transition-colors"
            aria-label="Kapat"
          >
            <Icon name="close" size={20} className="text-gray-700" />
          </button>
        </div>

        {/* Restaurant Info */}
        {restaurant && !isEmpty && (
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-3">
              {restaurant.logoUrl && (
                <img
                  src={restaurant.logoUrl}
                  alt={restaurant.name}
                  className="w-12 h-12 rounded-lg object-contain object-center border border-gray-200 bg-gray-50"
                />
              )}
              <div>
                <h3 className="font-semibold text-gray-900">{restaurant.name}</h3>
                <p className="text-sm text-gray-500">{restaurant.address}</p>
              </div>
            </div>
          </div>
        )}

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <Icon name="cart" size={64} className="mb-4" />
              <p className="text-lg font-medium">Sepetiniz boş</p>
              <p className="text-sm">Sipariş vermek için ürün ekleyin</p>
            </div>
          ) : (
            <div>
              {items.map((item: CartItem) => {
                const itemKey = getCartItemKey(item);
                
                return (
                  <CartItemComponent
                    key={itemKey}
                    item={item}
                    onIncrement={() => {
                      if (item.backendItemId) {
                        updateItem(item.backendItemId, item.quantity + 1).catch(err => {
                          console.error('Failed to update item:', err);
                        });
                      }
                    }}
                    onDecrement={() => {
                      if (item.backendItemId) {
                        if (item.quantity > 1) {
                          updateItem(item.backendItemId, item.quantity - 1).catch(err => {
                            console.error('Failed to update item:', err);
                          });
                        } else {
                          removeItem(item.backendItemId).catch(err => {
                            console.error('Failed to remove item:', err);
                          });
                        }
                      }
                    }}
                    onRemove={() => {
                      if (item.backendItemId) {
                        removeItem(item.backendItemId).catch(err => {
                          console.error('Failed to remove item:', err);
                        });
                      }
                    }}
                  />
                );
              })}

              {/* Clear Cart */}
              <div className="mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    clearCart().catch(err => {
                      console.error('Failed to clear cart:', err);
                    });
                  }}
                  className="text-red-600 hover:bg-red-50"
                >
                  Sepeti Temizle
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Summary & Checkout */}
        {!isEmpty && (
          <div className="border-t border-gray-200 p-4 bg-white">
            {/* Price Breakdown */}
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Ara Toplam</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Teslimat Ücreti</span>
                <span>{formatCurrency(deliveryFee)}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Hizmet Bedeli</span>
                <span>{formatCurrency(serviceFee)}</span>
              </div>
              {promotionDiscount && promotionDiscount > 0 && (
                <div className="flex justify-between text-sm text-[#2A9D8F] font-medium">
                  <span>
                    {appliedPromotionName ? `🎉 ${appliedPromotionName}` : 'Kampanya İndirimi'}
                  </span>
                  <span>-{formatCurrency(promotionDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-200">
                <span>Toplam</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>

            {/* Checkout Button */}
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={handleCheckout}
            >
              Siparişi Tamamla
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
